import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@bindery/ui/components/ui/popover";
import { cn } from "@bindery/ui/lib/utils";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import type { CSSProperties } from "react";
import { Link } from "react-router";
import { type NavPalette, variants } from "@/components/design-switcher";

interface DesignSwitcherProps {
  activeSlug: string;
  onSelect: (slug: string) => void;
}

export function DesignSwitcher({
  activeSlug,
  onSelect,
  palette,
}: DesignSwitcherProps & { palette?: NavPalette }) {
  const active = variants.find((v) => v.slug === activeSlug) ?? variants[0];
  const triggerStyle: CSSProperties | undefined = palette
    ? ({
        color: palette.text,
        "--nav-link-hover-bg": palette.surfaceHover,
      } as CSSProperties)
    : undefined;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          style={triggerStyle}
          className={cn(
            "flex items-center gap-1.5 rounded-xl px-2 py-1.5 text-[13px] font-semibold tracking-wide transition-all duration-150",
            palette
              ? "hover:bg-[var(--nav-link-hover-bg)]"
              : "text-white/60 hover:bg-white/[0.06] hover:text-white/90",
          )}
          aria-label={`Design: ${active?.name}. Click to change.`}
        >
          <span className="text-4xl leading-none" aria-hidden="true">
            {active?.emoji}
          </span>
          <ChevronDownIcon
            className={cn("h-10 w-10", !palette && "text-white/40")}
            style={palette ? { color: palette.textMuted } : undefined}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="center"
        sideOffset={8}
        className="z-[70] w-72 border-white/10 bg-black/80 p-0 text-white backdrop-blur-2xl"
      >
        <div className="border-b border-white/10 px-4 py-2.5">
          <span className="text-[11px] font-medium tracking-[0.2em] text-white/40 uppercase">
            Choose design
          </span>
        </div>
        <div className="max-h-[60vh] overflow-y-auto py-1">
          {variants.map((v) => {
            const isActive = v.slug === activeSlug;
            return (
              <button
                key={v.slug}
                type="button"
                onClick={() => onSelect(v.slug)}
                className={cn(
                  "flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-white/[0.08]",
                  isActive && "bg-white/[0.05]",
                )}
              >
                <div
                  className="h-7 w-7 shrink-0 rounded-md"
                  style={{
                    background: `linear-gradient(135deg, ${v.gradient[0]}, ${v.gradient[1]})`,
                  }}
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm leading-none" aria-hidden="true">
                      {v.emoji}
                    </span>
                    <span className="truncate text-sm font-medium text-white/90">
                      {v.name}
                    </span>
                  </div>
                  <span className="mt-0.5 block truncate text-xs text-white/40">
                    {v.tagline}
                  </span>
                </div>
                {isActive && (
                  <CheckIcon className="h-4 w-4 shrink-0 text-white/60" />
                )}
              </button>
            );
          })}
        </div>
        <div className="border-t border-white/10 px-4 py-2.5">
          <Link
            to="/gallery"
            className="flex items-center gap-1.5 text-xs font-medium text-white/50 transition-colors hover:text-white/80"
          >
            <span aria-hidden="true">🎨</span>
            Browse all in Gallery
            <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function DesignSwitcherCompact({
  activeSlug,
  onSelect,
}: DesignSwitcherProps) {
  return (
    <div>
      <span className="mb-2 block text-[11px] font-medium tracking-[0.2em] text-white/40 uppercase">
        Design
      </span>
      <div className="grid grid-cols-3 gap-2">
        {variants.map((v) => {
          const isActive = v.slug === activeSlug;
          return (
            <button
              key={v.slug}
              type="button"
              onClick={() => onSelect(v.slug)}
              className={cn(
                "relative flex flex-col items-center gap-1 rounded-lg p-2 transition-colors",
                isActive
                  ? "bg-white/10 ring-1 ring-white/20"
                  : "hover:bg-white/[0.06]",
              )}
              aria-label={`${v.name}${isActive ? " (active)" : ""}`}
            >
              <span className="text-4xl leading-none" aria-hidden="true">
                {v.emoji}
              </span>
              {isActive && (
                <div className="absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white/90">
                  <CheckIcon className="h-2.5 w-2.5 text-black" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
